import { fetchJson, IDP_TIMEOUT_MS } from './http';
import { IdpAuthError } from './provider';

/**
 * As chaves publicas do IDP, buscadas do `jwks_uri` do discovery.
 *
 * O contrato proibe segredo compartilhado: a assinatura e RS256 e qualquer um
 * valida com a chave publica (`proibicoes.md`, item 3). Quem seleciona a chave
 * e o `kid` do cabecalho do JWT — o realm publica mais de uma (assinatura e
 * cifragem) e rotaciona sem avisar ninguem.
 */

const CINCO_MINUTOS_MS = 300_000;

interface JsonWebKey$ {
  readonly kid?: string;
  readonly kty?: string;
  readonly alg?: string;
  readonly use?: string;
  readonly n?: string;
  readonly e?: string;
}

interface JsonWebKeySet {
  readonly keys?: readonly JsonWebKey$[];
}

const RS256_IMPORT = { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' } as const;

function isSigningKey(key: JsonWebKey$): boolean {
  // `use: "enc"` do Keycloak e chave de cifragem: importar como verificacao
  // falha, e pior, escolher a errada devolve "assinatura invalida" num token bom.
  return key.kty === 'RSA' && key.use !== 'enc' && (key.alg === undefined || key.alg === 'RS256');
}

export type JwksResolver = (kid: string | undefined) => Promise<CryptoKey>;

export interface JwksOptions {
  readonly jwksUri: () => Promise<string>;
  readonly fetchImpl?: typeof fetch;
  readonly timeoutMs?: number;
  /** Janela minima entre dois refetches disparados por `kid` desconhecido. */
  readonly cooldownMs?: number;
  readonly now?: () => number;
}

/**
 * Resolve o `kid` para uma `CryptoKey` pronta para `crypto.subtle.verify`.
 *
 * `kid` desconhecido dispara um refetch — e assim que rotacao de chave se
 * resolve sozinha — mas no maximo um por janela de cooldown, senao um token
 * forjado com `kid` aleatorio vira um GET no IDP por requisicao.
 */
export function createJwksResolver(options: JwksOptions): JwksResolver {
  const {
    jwksUri,
    fetchImpl,
    timeoutMs = IDP_TIMEOUT_MS,
    cooldownMs = CINCO_MINUTOS_MS,
    now = () => Date.now(),
  } = options;

  const keys = new Map<string, CryptoKey>();
  // So o refetch por `kid` desconhecido conta para o cooldown. Se o primeiro
  // carregamento contasse, uma rotacao logo depois dele ficaria sem a chave
  // nova ate a janela fechar.
  let lastUnknownKidFetch = Number.NEGATIVE_INFINITY;

  async function load(): Promise<void> {
    const uri = await jwksUri();
    const jwks = await fetchJson<JsonWebKeySet>(uri, {
      timeoutMs,
      ...(fetchImpl && { fetchImpl }),
    });
    keys.clear();

    await Promise.all(
      (jwks.keys ?? []).filter(isSigningKey).map(async (key) => {
        if (key.kid === undefined) return;
        const imported = await crypto.subtle.importKey(
          'jwk',
          key as JsonWebKey,
          RS256_IMPORT,
          false,
          ['verify'],
        );
        keys.set(key.kid, imported);
      }),
    );
  }

  return async (kid) => {
    if (kid === undefined) {
      throw new IdpAuthError('jwt_sem_kid', 'Token do IDP sem `kid` no cabecalho.');
    }
    if (keys.size === 0) await load();

    const known = keys.get(kid);
    if (known !== undefined) return known;

    if (now() - lastUnknownKidFetch >= cooldownMs) {
      lastUnknownKidFetch = now();
      await load();
    }

    const refreshed = keys.get(kid);
    if (refreshed === undefined) {
      throw new IdpAuthError('jwt_kid_desconhecido', `JWKS do IDP nao publica o kid ${kid}.`);
    }
    return refreshed;
  };
}
